import React, { useState } from 'react'
import { Alert, Platform } from 'react-native'
import { ArrowLeft } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import {
  Button, ButtonText, Card, Input, TextArea, Spinner, TopBar, IconButton,
  Text, XStack, YStack, ScreenLayout,
} from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'CreateMarket'>
}

export default function CreateMarketScreen({ navigation }: Props) {
  const { t } = useLanguage()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [address, setAddress] = useState('')
  const [commission, setCommission] = useState('10')
  const [loading, setLoading] = useState(false)

  const showAlert = (title: string, msg: string) => {
    if (Platform.OS === 'web') window.alert(msg)
    else Alert.alert(title, msg)
  }

  const createMarket = async () => {
    if (!name.trim()) {
      showAlert(t('error'), t('enter_market_name') || 'Please enter a market name')
      return
    }

    const commissionNum = parseFloat(commission)
    if (isNaN(commissionNum) || commissionNum < 0 || commissionNum > 100) {
      showAlert(t('error'), t('invalid_commission') || 'Commission must be between 0 and 100')
      return
    }

    setLoading(true)
    try {
      const { data, error } = await supabase.rpc('create_flea_market', {
        p_name: name.trim(),
        p_description: description.trim() || null,
        p_address: address.trim() || null,
        p_commission_percentage: commissionNum,
      })
      if (error) throw error
      showAlert(t('success'), t('market_created') || 'Market created successfully!')
      navigation.replace('MarketAdmin', { marketId: data })
    } catch (error: any) {
      console.error('Create error:', error)
      showAlert(t('error'), error.message || t('create_failed') || 'Failed to create market')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ScreenLayout>
      <TopBar
        title={t('create_market') || 'Create Market'}
        left={
          <IconButton variant="ghost" onPress={() => navigation.goBack()}>
            <ArrowLeft size={18} color="#4A90A4" />
          </IconButton>
        }
      />

      <YStack p="$md" gap="$lg" f={1}>
        <YStack gap="$sm">
          <Text preset="label">{t('market_name') || 'Market Name'} *</Text>
          <Input
            value={name}
            onChangeText={setName}
            placeholder={t('market_name_placeholder') || 'e.g. Helsinki Flea Market'}
          />
        </YStack>

        <YStack gap="$sm">
          <Text preset="label">{t('description') || 'Description'}</Text>
          <TextArea
            value={description}
            onChangeText={setDescription}
            placeholder={t('market_description_placeholder') || 'Describe your market...'}
            numberOfLines={4}
          />
        </YStack>

        <YStack gap="$sm">
          <Text preset="label">{t('address') || 'Address'}</Text>
          <Input
            value={address}
            onChangeText={setAddress}
            placeholder={t('address_placeholder') || 'Street address, city'}
          />
        </YStack>

        <YStack gap="$sm">
          <Text preset="label">{t('commission_rate') || 'Commission Rate (%)'}</Text>
          <Input
            value={commission}
            onChangeText={setCommission}
            placeholder="10"
            keyboardType="decimal-pad"
          />
          <Text preset="bodySmall" color="$colorSecondary">
            {t('commission_hint') || 'Percentage taken from each sale'}
          </Text>
        </YStack>

        <Button
          variant="primary"
          onPress={createMarket}
          disabled={loading}
          opacity={loading ? 0.6 : 1}
          mt="$md"
        >
          {loading ? <Spinner color="#fff" /> : <ButtonText>{t('create_market') || 'Create Market'}</ButtonText>}
        </Button>
      </YStack>
    </ScreenLayout>
  )
}
